import * as React from "react";
import ReactDOM = require("react-dom");
import Controls = require("VSS/Controls");
// import { WrappedComponent } from "VSS/Platform/Layout";
import { LWPComponent } from "VSSPreview/Flux/Components/LWP";
import { Column as ColumnRevision } from "Widgets/Scripts/DataServices/Legacy/AnalyticsChartingClient";
import { IListBoxItem } from "VSSUI/ListBox";

import { ProjectCollection } from "Presentation/Scripts/TFS/TFS.OM.Common";
import { CFDDataService } from "Widgets/Scripts/DataServices/Legacy/CumulativeFlowDiagramDataService";
import * as StringUtils from "VSS/Utils/String";
import Resources = require("Widgets/Scripts/Resources/TFS.Resources.Widgets");
import { Item } from "VersionControl/Scripts/Resources/TFS.Resources.VersionControl";

/** A new platform representation for Columns which includes a computed "IsDeleted" property. */
export interface Column {
    ColumnId: string;
    ColumnName: string;
    ColumnOrder: number;
    RevisedDate: string;
    IsDeleted: boolean;
}

export interface BoardColumnsPickerProps {
    // Notifies consumer that the selection has changed. We don't expose the content details, however.
    onSelect(): void;
}

export interface ColumnPickerProps {}

/** Encapsulates the Reporting ColumnPicker implemented under new platform. */
export class BoardColumnsPicker extends Controls.Control<BoardColumnsPickerProps> {
    private selectedIds?: string[];
    private allColumns: IListBoxItem<Column>[];
    private activeColumns: IListBoxItem<Column>[];

    public initializeOptions(options?: BoardColumnsPickerProps): void {
        super.initializeOptions(
            $.extend(
                {
                    coreCssClass: "boards-columns-picker"
                },
                options
            )
        );
        this.selectedIds = [];
        this.allColumns = [];
        this.activeColumns = [];
    }

    public initialize(): void {
        this.render();
    }

    public render(): void {
        if (this.activeColumns == null || this.selectedIds == null) {
            return null;
        }

        const renderProps = {
            items: this.activeColumns,
            selectedIds: this.selectedIds,
            onSelect: (columns: IListBoxItem<Column>[]) => {
                this.onSelect(columns);
            }
        };
        const container = this.getElement()[0];
        const element = ReactDOM.render<any>(
            <LWPComponent wrappedType={"BoardsColumnPicker"} dependencies={["ms.vss-reporting-web.cfd-report-content"]} wrappedProps={renderProps} />,
            container
        ) as React.Component<any, any>;
    }

    private onSelect = (columns: IListBoxItem<Column>[]): void => {
        this.selectedIds = columns.map(item => {
            return item.id;
        });
        this._options.onSelect();
    }

    /**
     * Accomodates CFD config pattern for populating service driven state after UI creation
     * Retrieves the columns for the provided parameters and populates the column selector with the returned data
     * @param {string} project - Name or ID of a team project
     * @param {string} boardId - ID of the specific board
     * @return A promise that resolves when the swimlane selector is populated with the retrieved data
     */
    public async setContext(projectId: string, boardId: string): Promise<void> {
        const connection = ProjectCollection.getDefaultConnection();
        const cfdDataService = connection.getService(CFDDataService);
        const columns = filterColumnRevisionsToLatest(await cfdDataService.getBoardColumns(projectId, boardId));
        const orderedColumns = orderBoardColumns(columns);
        this.allColumns = orderedColumns.map(column => {
            return {
                id: column.ColumnId,
                text: column.IsDeleted ? StringUtils.format(Resources.DeletedBoardColumnName, column.ColumnName) : column.ColumnName,
                data: column
            } as IListBoxItem<Column>;
        });
        this.render();
        return;
    }

    /** Encapsulates CFD Config req of exposing settings for assembly - Exposes board ID. */
    public getSettings(): string[] {
        return this.selectedIds;
    }

    /** Encapsulates CFD Config pattern of a step to manipulate inclusion of available columns to omit unsupported first column outside of default or all lane scenario  */
    public setAllowedColumns(allowFirstColumn: boolean): void {
        this.activeColumns = [...this.allColumns];
        if (!allowFirstColumn) {
            const firstColumn = this.activeColumns.pop();
            const id = firstColumn.id;
            this.selectedIds = this.selectedIds.filter(candidateId => {
                return id != candidateId;
            });
        }
        this.render();
    }

    public setSelection(initialSelection: string[]): void {
        if (initialSelection != null) {
            this.selectedIds = initialSelection;
        } else {
            // Wonky Policy: This compensates for incomplete context from CFD init and event handlers. This condition avoids stomping on initial configuration after load.
            // If we're given an empty selection set - conditionally reset the selection.
            // Conditional on if there is no existing selection, or the selection was referring to unrecognized column ids of a different board.
            if (
                this.selectedIds.length === 0 ||
                !this.activeColumns.some(item => {
                    return item.id === this.selectedIds[0];
                })
            ) {
                this.selectedIds = this.activeColumns
                    .filter(activeColumn => activeColumn.data.ColumnOrder !== 0) // CFD policy: Don't include the first board column in the smart defaults
                    .map(item => item.id);
            }
        }

        this.render();
    }
}

/**
 * Applies CFD Chart style column ordering, as ported from legacy.
 * @param boardColumns
 */
function orderBoardColumns(boardColumns: ReadonlyArray<Column>): Column[] {
    const activeColumns: Column[] = [];
    const deletedColumns: Column[] = [];

    for (const column of boardColumns) {
        if (column.IsDeleted) {
            deletedColumns.push(column);
        } else {
            activeColumns.push(column);
        }
    }

    // Order deleted columns by deletion date (latest to earliest)
    deletedColumns.sort((a, b) => {
        const revisedDateA = new Date(a.RevisedDate);
        const revisedDateB = new Date(b.RevisedDate);
        return revisedDateB.getTime() - revisedDateA.getTime();
    });

    // Order active columns by column order (last to first)
    activeColumns.sort((a, b) => b.ColumnOrder - a.ColumnOrder);

    // Insert sorted deleted columns immediately after the Done state column of the ordered active columns
    // [startIndex, deleteCount, ...items]
    const orderedColumns = activeColumns.slice();
    orderedColumns.splice(1, 0, ...deletedColumns);

    return orderedColumns;
}

export function filterColumnRevisionsToLatest(revisions: ColumnRevision[]): Column[] {
    // Back-ported from New plat BoardColumnsQuery
    const endOfTime: number = new Date(Date.UTC(9999, 0, 1)).getTime();
    const latestColumnRevisionsMap: { [key: string]: ColumnRevision } = {};
    for (const revision of revisions) {
        const latestKnownRevision = latestColumnRevisionsMap[revision.ColumnId];
        if (latestKnownRevision != null) {
            const columnRevisedDate = new Date(revision.LatestRevisedDate);
            const latestKnownRevisedDate = new Date(latestKnownRevision.LatestRevisedDate);

            // Save the latest revision
            if (columnRevisedDate.getTime() > latestKnownRevisedDate.getTime()) {
                latestColumnRevisionsMap[revision.ColumnId] = revision;
            }
        } else {
            latestColumnRevisionsMap[revision.ColumnId] = revision;
        }
    }

    // Map to array
    const latestColumnRevisions: Column[] = [];
    for (const key in latestColumnRevisionsMap) {
        const revision = latestColumnRevisionsMap[key];
        const revisedDate = new Date(revision.LatestRevisedDate);
        const isDeleted = revisedDate.getTime() !== endOfTime;

        latestColumnRevisions.push({
            ColumnId: revision.ColumnId,
            ColumnName: revision.ColumnName,
            ColumnOrder: revision.ColumnOrder,
            RevisedDate: revision.LatestRevisedDate,
            IsDeleted: isDeleted
        });
    }

    return latestColumnRevisions;
}
